// FeedbackManager.js - Admin panel for managing all feedback
import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';
import api from '../../utils/api';
import FeedbackDetail from './FeedbackDetail';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import FeedbackItem from './FeedbackItem';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${THEME.zIndex.modal};
  animation: ${fadeIn} ${THEME.transitionFast};
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: 16px;
  width: 95%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} ${THEME.transitionMedium};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.lg} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.inputBorder};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${THEME.fontSize.xl};
  color: ${THEME.textPrimary};
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  padding: ${THEME.spacing.md} ${THEME.spacing.xl};
  border-bottom: 1px solid ${THEME.inputBorder};
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${THEME.spacing.xs};
`;

const FilterButton = styled.button`
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  background: ${props => props.$active ? THEME.accentPrimary : 'transparent'};
  color: ${props => props.$active ? 'white' : THEME.textSecondary};
  border: 1px solid ${props => props.$active ? THEME.accentPrimary : THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    border-color: ${THEME.accentPrimary};
    color: ${props => props.$active ? 'white' : THEME.textPrimary};
  }
`;

const Select = styled.select`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }
`;

const SearchInput = styled.input`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.xs} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.sm};
  width: 200px;

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const ListPanel = styled.div`
  width: 50%;
  border-right: 1px solid ${THEME.inputBorder};
  overflow-y: auto;
  padding: ${THEME.spacing.md};
`;

const DetailPanel = styled.div`
  width: 50%;
  overflow-y: auto;
  padding: ${THEME.spacing.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${THEME.spacing.xxl};
  color: ${THEME.textSecondary};
`;

const ItemCount = styled.span`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.sm};
  margin-left: ${THEME.spacing.sm};
`;

const FeedbackManager = ({ isOpen, onClose, onLoadProject }) => {
  const [feedback, setFeedback] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isOpen) {
      loadFeedback();
      api.markAdminFeedbackViewed().catch(console.error);
    }
  }, [isOpen]);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const data = await api.getAdminFeedback();
      setFeedback(data);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...feedback];

    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.username.toLowerCase().includes(query)
      );
    }

    setFilteredFeedback(filtered);
  }, [feedback, typeFilter, statusFilter, searchQuery]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = filteredFeedback.findIndex(item => item.id === active.id);
      const newIndex = filteredFeedback.findIndex(item => item.id === over.id);

      const newOrder = arrayMove(filteredFeedback, oldIndex, newIndex);
      setFilteredFeedback(newOrder);

      // Update queue positions
      const items = newOrder.map((item, index) => ({
        id: item.id,
        queue_position: index + 1
      }));

      try {
        await api.reorderFeedback(items);
        // Update main feedback array
        const updatedFeedback = feedback.map(item => {
          const newPos = items.find(i => i.id === item.id);
          return newPos ? { ...item, queue_position: newPos.queue_position } : item;
        });
        setFeedback(updatedFeedback);
      } catch (error) {
        console.error('Failed to reorder:', error);
        // Revert on error
        setFilteredFeedback([...filteredFeedback]);
      }
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleItemUpdate = async (id, updates) => {
    try {
      const result = await api.updateAdminFeedback(id, updates);
      // Update local state
      setFeedback(prev => prev.map(item =>
        item.id === id ? { ...item, ...result.feedback } : item
      ));
      if (selectedItem?.id === id) {
        setSelectedItem(prev => ({ ...prev, ...result.feedback }));
      }
    } catch (error) {
      console.error('Failed to update:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedItem) {
          setSelectedItem(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, selectedItem]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Feedback Manager
            <ItemCount>({filteredFeedback.length} items)</ItemCount>
          </ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            ✕
          </CloseButton>
        </ModalHeader>

        <FilterBar>
          <FilterGroup>
            <FilterButton
              $active={typeFilter === 'all'}
              onClick={() => setTypeFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton
              $active={typeFilter === 'bug'}
              onClick={() => setTypeFilter('bug')}
            >
              Bugs
            </FilterButton>
            <FilterButton
              $active={typeFilter === 'feature'}
              onClick={() => setTypeFilter('feature')}
            >
              Features
            </FilterButton>
          </FilterGroup>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="wont_fix">Won't Fix</option>
          </Select>

          <SearchInput
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </FilterBar>

        <ContentArea>
          <ListPanel>
            {loading ? (
              <LoadingState>Loading feedback...</LoadingState>
            ) : filteredFeedback.length === 0 ? (
              <EmptyState>No feedback items found</EmptyState>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredFeedback.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredFeedback.map(item => (
                    <FeedbackItem
                      key={item.id}
                      item={item}
                      isSelected={selectedItem?.id === item.id}
                      onSelect={() => handleItemSelect(item)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </ListPanel>

          <DetailPanel>
            {selectedItem ? (
              <FeedbackDetail
                item={selectedItem}
                onUpdate={handleItemUpdate}
                onLoadProject={onLoadProject}
              />
            ) : (
              <EmptyState>Select an item to view details</EmptyState>
            )}
          </DetailPanel>
        </ContentArea>
      </Modal>
    </Overlay>
  );
};

export default FeedbackManager;
